import type { Game } from '../../game/Game';
import type { ZoneOpts } from '../../game/netHooks';
import type { Progression } from '../../game/Progression';
import type { ZoneRuntime } from '../../game/ZoneRuntime';
import { type RewardsState, bump, closePanel, ui } from '../../ui/app/store';
import { syncCharacter } from '../bounties/board';
import { type ActiveScript, createScript } from './attach';
import type { AnnounceStyle, InstanceEnv } from './env';
import { GateScript } from './gates';
import { instUi, setActionHandler } from './hudStore';
import { nextZoneOpts } from './script';

/**
 * The browser-side `InstanceEnv`: attaches the instance script to each zone
 * the game loads, feeds the HUD / death / result overlays, and performs zone
 * moves after the frame (a script must never dispose the zone it runs in).
 *
 * Engine workarounds (no Game.ts edits):
 * - Gate portals in the raid layouts call `game.enterInstance(next, {})`,
 *   which would drop the run's options (difficulty, group room). The client
 *   wraps `enterInstance` so that walking into the next gate's portal moves
 *   the group into `<root>:<zone>` with the run's options instead.
 * - The game's default death screen only offers checkpoint / plume revives;
 *   in instance zones the script's own death screen replaces it.
 */
export class InstanceClient implements InstanceEnv {
  readonly memo = new Map<string, unknown>();
  readonly visual = true;
  active: ActiveScript | null = null;
  private gate: GateScript | null = null;
  private pending: (() => void) | null = null;
  private uiT = 0;

  constructor(readonly game: Game) {
    setActionHandler((id) => this.active?.action(id));
  }

  // ---------------------------------------------------------------- InstanceEnv

  get progression(): Progression | null {
    return this.game.progression;
  }

  announce(text: string, style: AnnounceStyle = 'info'): void {
    this.game.hud.announce(text, style);
  }

  toast(text: string, color?: string): void {
    this.game.hud.toast(text, color);
  }

  rewards(r: RewardsState): void {
    ui.rewards.value = r;
    bump();
  }

  transition(zone: string, opts: ZoneOpts): void {
    this.pending = () => this.go(zone, opts);
  }

  leave(): void {
    this.pending = () => this.game.leaveInstance();
  }

  revived(): void {
    ui.deathOptions.value = null;
    instUi.death.value = null;
    this.game.renderer.grade.desaturate = 0;
  }

  syncPeriods(): void {
    const p = this.game.progression;
    if (p) syncCharacter(p);
  }

  save(): void {
    this.game.store.saveSoon(this.game.account);
    bump();
  }

  // ---------------------------------------------------------------- wiring

  /** Zone setup: attaches the zone's instance script (if any). */
  setup(z: ZoneRuntime, opts: ZoneOpts): void {
    this.active = null;
    this.gate = null;
    this.clearUi();
    const s = createScript(z.layout.id, z.layout.kind, this, opts);
    if (!s) return;
    z.scripts.push(s);
    this.active = s;
    if (s instanceof GateScript) this.gate = s;
    z.scripts.push({
      dispose: () => {
        if (this.active !== s) return;
        this.active = null;
        this.gate = null;
        this.clearUi();
      },
    });
  }

  private clearUi(): void {
    instUi.hud.value = null;
    instUi.death.value = null;
    instUi.result.value = null;
  }

  /** Moves to another zone of the run (no ready check, keeps the return point). */
  private go(zone: string, opts: ZoneOpts): void {
    const g = this.game;
    if (g.loading || !g.zone) return;
    closePanel();
    ui.dialogue.value = null;
    ui.screen.value = 'loading';
    g.enterZone(zone, 'start', opts)
      .then(() => {
        ui.screen.value = 'game';
      })
      .catch((e: unknown) => {
        console.error('instance transition failed', e);
        ui.screen.value = 'game';
      });
  }

  /** Walking into the next gate's portal keeps the run together (see class notes). */
  wrapEnterInstance(): void {
    const g = this.game;
    const orig = g.enterInstance.bind(g);
    g.enterInstance = (id: string, opts: ZoneOpts = {}) => {
      const gate = this.gate;
      const z = g.zone;
      if (gate && z && gate.def.next === id && typeof opts.inst !== 'string' && opts.run === undefined) {
        this.go(id, nextZoneOpts(gate.opts, id, z.world.hostPeer, { run: gate.run }));
        return;
      }
      orig(id, opts);
    };
  }

  /** `station` interactables placed by gate scripts (resupply). */
  handleStation(id: string): boolean {
    if (!id.startsWith('inst_resupply')) return false;
    this.active?.action('resupply');
    return true;
  }

  /** Per-frame system: deferred zone moves and overlay state. */
  system(dt: number): void {
    if (this.pending) {
      const f = this.pending;
      this.pending = null;
      f();
      return;
    }
    const s = this.active;
    if (!s || !this.game.zone) return;
    const me = this.game.player;
    const death = me && !me.alive ? s.deathView() : null;
    // The script's death screen replaces the game's default one.
    if (death && ui.deathOptions.value) ui.deathOptions.value = null;
    this.uiT -= dt;
    const deathChanged = !!death !== !!instUi.death.value;
    if (this.uiT > 0 && !deathChanged) return;
    this.uiT = 0.2;
    instUi.hud.value = s.view();
    instUi.death.value = death;
    instUi.result.value = s.resultView();
  }
}
